import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import AltRouteIcon from '@mui/icons-material/AltRoute';
import { type ImportedLocation } from 'api/location-importer.api';
import { type RouteDistancePathOption } from 'api/haul-route.api';
import { formatDistance } from './roadsImporter.helpers';

interface FindRoutePanelProps {
  locations: ImportedLocation[];
  from: ImportedLocation | null;
  to: ImportedLocation | null;
  via: ImportedLocation[];
  onChangeFrom: (loc: ImportedLocation | null) => void;
  onChangeTo: (loc: ImportedLocation | null) => void;
  onChangeVia: (locs: ImportedLocation[]) => void;
  onFind: () => void;
  onClose: () => void;
  loading: boolean;
  results: RouteDistancePathOption[] | null;
  selectedIndex: number | null;
  onSelect: (index: number) => void;
}

export function FindRoutePanel({
  locations,
  from,
  to,
  via,
  onChangeFrom,
  onChangeTo,
  onChangeVia,
  onFind,
  onClose,
  loading,
  results,
  selectedIndex,
  onSelect,
}: FindRoutePanelProps): JSX.Element {
  const { t } = useTranslation();

  const sortedLocations = useMemo(
    () => [...locations].sort((a, b) => a.name.localeCompare(b.name)),
    [locations]
  );

  const viaOptions = sortedLocations.filter(
    (loc) =>
      loc.locationId !== from?.locationId && loc.locationId !== to?.locationId
  );

  const sameLocation =
    from != null && to != null && from.locationId === to.locationId;

  return (
    <Box
      sx={{
        position: 'absolute',
        top: 12,
        left: 12,
        zIndex: 6,
        width: 340,
        maxHeight: 'calc(100% - 24px)',
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        px: 2,
        py: 1.5,
        borderRadius: 2,
        bgcolor: (th) => alpha(th.palette.background.paper, 0.96),
        border: '1px solid',
        borderColor: 'divider',
        boxShadow: 4,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <AltRouteIcon sx={{ color: 'primary.main' }} />
        <Typography
          variant="subtitle1"
          sx={{ fontWeight: 600, flex: 1, color: 'text.primary' }}
        >
          {t('roads_importer_find_route_title')}
        </Typography>
        <Tooltip title={t('roads_importer_btn_close')}>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1.25,
          // No overflow/scroll on this box — form controls stay in normal
          // flow. Setting overflow-y here (even just to fit the results
          // list) forces the browser to also clip overflow-x per the CSS
          // spec, cutting off the Switch's negative left margin and the
          // Autocomplete's shrunk floating label. Only the results list
          // below gets its own bounded, independently-scrollable box.
        }}
      >
        <Autocomplete
          size="small"
          options={sortedLocations}
          getOptionLabel={(loc) => loc.name}
          value={from}
          onChange={(_, loc) => {
            onChangeFrom(loc);
          }}
          isOptionEqualToValue={(a, b) => a.locationId === b.locationId}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t('roads_importer_find_route_from')}
            />
          )}
        />

        <Autocomplete
          size="small"
          options={sortedLocations}
          getOptionLabel={(loc) => loc.name}
          value={to}
          onChange={(_, loc) => {
            onChangeTo(loc);
          }}
          isOptionEqualToValue={(a, b) => a.locationId === b.locationId}
          renderInput={(params) => (
            <TextField {...params} label={t('roads_importer_find_route_to')} />
          )}
        />

        <Autocomplete
          multiple
          size="small"
          options={viaOptions}
          getOptionLabel={(loc) => loc.name}
          value={via}
          onChange={(_, locs) => {
            onChangeVia(locs);
          }}
          isOptionEqualToValue={(a, b) => a.locationId === b.locationId}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t('roads_importer_find_route_via')}
              placeholder={
                via.length === 0
                  ? t('roads_importer_find_route_via_placeholder')
                  : undefined
              }
            />
          )}
        />

        {sameLocation && (
          <Typography variant="caption" sx={{ color: 'error.main' }}>
            {t('roads_importer_find_route_same_location')}
          </Typography>
        )}

        <Button
          variant="contained"
          size="small"
          disabled={!from || !to || sameLocation || loading}
          startIcon={
            loading ? <CircularProgress size={14} color="inherit" /> : null
          }
          onClick={onFind}
          sx={{ borderRadius: '8px', fontWeight: 600 }}
        >
          {t('roads_importer_find_route_button')}
        </Button>

        {results !== null && (
          <>
            <Divider />
            {results.length === 0 ? (
              <Typography
                variant="body2"
                sx={{ color: 'text.secondary', textAlign: 'center', py: 1 }}
              >
                {t('roads_importer_find_route_no_results')}
              </Typography>
            ) : (
              <Box
                sx={{ overflowY: 'auto', maxHeight: 280, mx: -0.5, px: 0.5 }}
              >
                <List dense disablePadding>
                  {results.map((option, idx) => (
                    <ListItemButton
                      key={`${
                        option.totalDistanceMeters
                      }-${option.locationIds.join(',')}`}
                      selected={idx === selectedIndex}
                      onClick={() => {
                        onSelect(idx);
                      }}
                      sx={{
                        borderRadius: 1.5,
                        mb: 0.5,
                        border: '1px solid',
                        borderColor:
                          idx === selectedIndex ? 'primary.main' : 'divider',
                      }}
                    >
                      <ListItemText
                        primary={
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 600, color: 'text.primary' }}
                          >
                            {formatDistance(option.totalDistanceMeters)}
                          </Typography>
                        }
                        secondary={
                          <Typography
                            variant="caption"
                            sx={{ color: 'text.secondary' }}
                          >
                            {option.locationNames.join(' → ')}
                          </Typography>
                        }
                      />
                    </ListItemButton>
                  ))}
                </List>
              </Box>
            )}
          </>
        )}
      </Box>
    </Box>
  );
}
